import { IMAGE_PLACEHOLDER, mediaUrlOrPlaceholder } from '@/utils/imagePlaceholder'

const imageBase = () => (process.env.VUE_APP_IMAGE_PATH || '').replace(/\/+$/, '')

export { IMAGE_PLACEHOLDER }

/**
 * Resolve API image path to full URL.
 * Empty / missing paths resolve to the shared admin placeholder.
 */
export function resolveMediaUrl(path, options = {}) {
  const allowEmpty = options.allowEmpty === true
  if (path === null || path === undefined || path === '') {
    return allowEmpty ? '' : IMAGE_PLACEHOLDER
  }

  const trimmed = mediaUrlOrPlaceholder(path)
  if (trimmed === IMAGE_PLACEHOLDER) {
    return allowEmpty ? '' : IMAGE_PLACEHOLDER
  }

  if (/^(https?:\/\/|data:|blob:)/i.test(trimmed)) return trimmed
  if (trimmed.startsWith('/')) return trimmed

  const base = imageBase()
  if (!base) return trimmed
  return `${base}/${trimmed.replace(/^\/+/, '')}`
}

/** Extract payload from admin API axios response. */
export function unwrapAdminResponse(res) {
  const body = res?.data ?? {}
  if (body.data !== undefined && body.data !== null) {
    return body.data
  }
  return body
}

/**
 * Parse list + total from ApiResponse paginated (`data` array + `meta`) or AdminApiResponse (`data.pages` + `pagination`).
 */
export function unwrapPaginatedList(res, listKeys = []) {
  const body = res?.data ?? {}
  const inner = body.data !== undefined && body.data !== null ? body.data : body

  if (Array.isArray(inner)) {
    return {
      items: inner,
      total: Number(body.meta?.total ?? inner.length),
      meta: body.meta ?? null,
    }
  }

  for (const key of listKeys) {
    if (Array.isArray(inner?.[key])) {
      const pagination = inner.pagination ?? body.pagination ?? {}
      return {
        items: inner[key],
        total: Number(pagination.total ?? inner[key].length),
        meta: pagination,
      }
    }
  }

  if (Array.isArray(inner?.items)) {
    const pagination = inner.pagination ?? body.pagination ?? {}
    return {
      items: inner.items,
      total: Number(pagination.total ?? inner.items.length),
      meta: pagination,
    }
  }

  return { items: [], total: 0, meta: body.meta ?? body.pagination ?? null }
}

/** Format API date values as MM-dd-yyyy for admin UI. */
export function formatAdminDate(value) {
  if (value === null || value === undefined || value === '') return ''
  const trimmed = String(value).trim()
  if (/^\d{2}-\d{2}-\d{4}$/.test(trimmed)) return trimmed
  const parsed = new Date(trimmed)
  if (Number.isNaN(parsed.getTime())) return trimmed
  const mm = String(parsed.getMonth() + 1).padStart(2, '0')
  const dd = String(parsed.getDate()).padStart(2, '0')
  const yyyy = parsed.getFullYear()
  return `${mm}-${dd}-${yyyy}`
}

/** Convert admin UI date (m/d/Y or MM-dd-yyyy) to Y-m-d for API query params. */
export function parseAdminDateForApi(value) {
  if (value === null || value === undefined || value === '') return undefined
  const trimmed = String(value).trim()
  const slashMatch = trimmed.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/)
  if (slashMatch) {
    const [, month, day, year] = slashMatch
    return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`
  }
  const dashMatch = trimmed.match(/^(\d{2})-(\d{2})-(\d{4})$/)
  if (dashMatch) {
    const [, month, day, year] = dashMatch
    return `${year}-${month}-${day}`
  }
  const parsed = new Date(trimmed)
  if (Number.isNaN(parsed.getTime())) return undefined
  const mm = String(parsed.getMonth() + 1).padStart(2, '0')
  const dd = String(parsed.getDate()).padStart(2, '0')
  const yyyy = parsed.getFullYear()
  return `${yyyy}-${mm}-${dd}`
}

/** Convert admin DOB display value (MM/DD/YYYY) to UTC ISO string for API payloads. */
export function formatDobForApi(value) {
  if (value === null || value === undefined || value === '') return null
  const trimmed = String(value).trim()
  if (/^\d{4}-\d{2}-\d{2}T/.test(trimmed)) {
    const parsed = new Date(trimmed)
    if (!Number.isNaN(parsed.getTime())) {
      return parsed.toISOString()
    }
  }
  const ymd = parseAdminDateForApi(value)
  if (!ymd) return null
  const [year, month, day] = ymd.split('-').map(Number)
  return new Date(Date.UTC(year, month - 1, day, 0, 0, 0, 0)).toISOString()
}

/** Convert API DOB (ISO or display) to MM/DD/YYYY for admin date pickers. */
export function formatDobForDisplay(value) {
  if (value === null || value === undefined || value === '') return ''
  const trimmed = String(value).trim()
  if (/^\d{1,2}\/\d{1,2}\/\d{4}$/.test(trimmed)) return trimmed
  const parsed = new Date(trimmed)
  if (Number.isNaN(parsed.getTime())) return trimmed
  const mm = String(parsed.getUTCMonth() + 1).padStart(2, '0')
  const dd = String(parsed.getUTCDate()).padStart(2, '0')
  const yyyy = parsed.getUTCFullYear()
  return `${mm}/${dd}/${yyyy}`
}

/** Normalize pagination from various admin response shapes. */
export function normalizePagination(payload, fallback = {}, res = null) {
  const body = res?.data ?? {}
  const inner = body.data !== undefined && body.data !== null ? body.data : body
  const top = body.pagination ?? body.meta
  const nested = inner?.pagination ?? inner?.meta
  const p = payload?.pagination ?? nested ?? top ?? payload ?? {}
  return {
    currentPage: Number(p.current_page ?? p.currentPage ?? fallback.currentPage ?? 1),
    perPage: Number(p.per_page ?? p.perPage ?? fallback.perPage ?? 10),
    total: Number(p.total ?? p.total_records ?? fallback.total ?? 0),
    lastPage: Number(p.last_page ?? p.total_pages ?? fallback.lastPage ?? 1),
  }
}

/** "Showing X to Y of Z entries" label for admin tables. */
export function paginationShowingLabel(page, perPage, total) {
  const safeTotal = Number(total) || 0
  const safePage = Number(page) || 1
  const safePerPage = Number(perPage) || 10
  if (!safeTotal) return 'No entries'
  const start = (safePage - 1) * safePerPage + 1
  const end = Math.min(safePage * safePerPage, safeTotal)
  return `Showing ${start} to ${end} of ${safeTotal} entries`
}

/**
 * Apply pagination meta from an admin API response onto component state.
 * Returns normalized pagination object.
 */
export function applyListPagination(vm, res, payload, options = {}) {
  const pageKey = options.pageKey || 'currentPage'
  const totalKey = options.totalKey || 'totalRows'
  const pagination = normalizePagination(payload, {}, res)
  vm[totalKey] = pagination.total
  const lastPage = Math.max(1, pagination.lastPage || 1)
  if (vm[pageKey] > lastPage) {
    vm[pageKey] = lastPage
  }
  return pagination
}

export function debounce(fn, wait = 400) {
  let timer
  return function debounced(...args) {
    clearTimeout(timer)
    timer = setTimeout(() => fn.apply(this, args), wait)
  }
}

/** Statuses that allow admin approve/reject moderation (matches API AdminAdWorkflow). */
export const AD_PENDING_STATUSES = ['pending', 'pending_approval', 'uploaded', 'draft']

export function isAdPendingStatus(status) {
  const normalized = String(status || '').toLowerCase().trim()
  if (!normalized) return true
  return AD_PENDING_STATUSES.includes(normalized) || normalized.includes('pend')
}

/** Prefer validation field errors, then API message. */
export function extractApiErrorMessage(error, fallback = 'Something went wrong.') {
  const errors = error?.response?.data?.errors
  if (errors && typeof errors === 'object') {
    const first = Object.values(errors).flat().find(Boolean)
    if (first) return String(first)
  }
  return error?.response?.data?.message || fallback
}
